import type { ReactNode } from 'react'
import { Phone } from 'lucide-react'
import { cn } from '@/shared/lib/cn.ts'
import { CONTACT_NAMES, type ContactLink } from '../contacts.ts'

const opensNewTab = (href: string): boolean => href.startsWith('https://')

function hint(link: ContactLink): string {
  const base = `${CONTACT_NAMES[link.kind]}: ${link.label}`
  if (link.byPhone) return `${base} — по номеру телефона, откроется, если номер есть в ${CONTACT_NAMES[link.kind]}`
  return link.preferred ? `${base} — салону так удобнее` : base
}

function LinkTo({ link, className, children }: { link: ContactLink; className: string; children: ReactNode }) {
  return (
    <a
      href={link.href}
      title={hint(link)}
      {...(opensNewTab(link.href) ? { target: '_blank', rel: 'noreferrer' } : {})}
      className={className}
    >
      {children}
    </a>
  )
}

// Chips built from a phone number are dashed: the salon never said it uses that messenger.
const chipClass = (link: ContactLink) =>
  cn(
    'inline-flex items-center rounded-full border px-2 py-0.5 text-[12px] leading-[18px] transition-colors',
    link.preferred
      ? 'border-[#bfd4c5] bg-brand-tint text-brand-ink hover:border-brand'
      : 'border-line bg-surface text-neutral-ink hover:border-[#d8d2c6] hover:text-ink',
    link.byPhone && 'border-dashed',
  )

/** Small chat chips under a line of text, e.g. the owner's phone. */
export function ChatChips({ links }: { links: ContactLink[] }) {
  if (links.length === 0) return null
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {links.map((link) => (
        <LinkTo key={link.href} link={link} className={chipClass(link)}>
          {CONTACT_NAMES[link.kind]}
        </LinkTo>
      ))}
    </div>
  )
}

/** Table cell: numbers to call, then a small chip per messenger. */
export function ContactLine({ links }: { links: ContactLink[] }) {
  if (links.length === 0) return <div className="mt-1 text-[12px] text-ink-faint">контактов нет</div>

  const phones = links.filter((link) => link.kind === 'phone')
  const others = links.filter((link) => link.kind !== 'phone')

  return (
    <div className="mt-1.5 flex flex-col gap-1">
      {phones.map((link) => (
        <LinkTo key={link.href} link={link} className="tabular w-fit text-[12.5px] whitespace-nowrap text-ink hover:text-brand hover:underline">
          {link.label}
        </LinkTo>
      ))}
      {others.length > 0 && <ChatChips links={others} />}
    </div>
  )
}

/** Phone card and review dialog: one tap target per way to reach the salon. */
export function ContactButtons({ links }: { links: ContactLink[] }) {
  if (links.length === 0) return <span className="text-ink-faint">контактов нет</span>

  return (
    <div className="flex flex-wrap gap-1.5">
      {links.map((link) => (
        <LinkTo
          key={link.href}
          link={link}
          className={cn(
            'inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-full border px-3 text-[13.5px] transition-colors md:min-h-9',
            link.preferred
              ? 'border-[#bfd4c5] bg-brand-tint text-brand-ink hover:border-brand'
              : 'border-line bg-surface text-ink hover:border-[#d8d2c6]',
            link.byPhone && 'border-dashed',
          )}
        >
          {link.kind === 'phone' ? (
            <>
              <Phone className="size-3.5 shrink-0" aria-hidden />
              <span className="tabular truncate">{link.label}</span>
            </>
          ) : (
            <>
              <span className="font-medium">{CONTACT_NAMES[link.kind]}</span>
              {/* On a phone the names fit in a row; the number or @handle is in the link title. */}
              {link.label !== CONTACT_NAMES[link.kind] && (
                <span className="truncate text-ink-muted max-md:hidden">{link.label}</span>
              )}
            </>
          )}
        </LinkTo>
      ))}
    </div>
  )
}
